'use client';

import { type StockItemResult } from '@app/(darkHeader)/ai/_shared/_actions/searchLibraryImages';
import { ImageResultsCard } from '@app/(darkHeader)/ai/_shared/_components/ImageResultsCard/ImageResultsCard';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { type JSX } from 'react';

import { Icon, JustifiedGallery, Pagination } from '@videoblocks/design-system';
import { WarningTriangle } from '@videoblocks/react-icons';

import { GenerationFlowLayout } from '../../../../_shared/_layouts/GenerationFlow/GenerationFlowLayout';
import { useVideoGenerationForm } from '../../useVideoGenerationForm';
import { CurrentGenerationCta } from './CurrentGenerationCta';

const NoResultsState = (): JSX.Element => (
    <div data-testid="search-no-results" className="flex-1 flex flex-col">
        <GenerationFlowLayout
            title="No matching images found"
            subtitle="Try a different search term or check your spelling."
            icon={<Icon name="image" width={48} height={48} aria-hidden="true" />}
        />
    </div>
);

const ErrorState = (): JSX.Element => (
    <div data-testid="search-error" className="flex-1 flex flex-col">
        <GenerationFlowLayout
            title="Something went wrong"
            subtitle="This request couldn't be completed. Please try again"
            icon={
                <WarningTriangle
                    className="size-8 dark:[&_path]:fill-neutral-dark-1000"
                    data-testid="search-error-icon"
                />
            }
        />
    </div>
);

export const SearchLayout = (): JSX.Element => {
    const { imageSearch, activeTask } = useAiToolkitContext();
    const { results, isLoading, error, hasSearched, currentPage, totalPages, goToPage } = imageSearch;
    const { formState, onLibraryImageSelect, onFileRemove } = useVideoGenerationForm();
    const { selectedLibraryImage } = formState;
    const isFormDisabled = !!activeTask && activeTask.status === 'inProgress';

    const handleSelect = (item: StockItemResult): void => {
        if (isFormDisabled) return;
        if (item.contentId === selectedLibraryImage?.contentId) {
            onFileRemove();
            return;
        }
        onLibraryImageSelect(item);
    };

    const showError = !isLoading && !!error;
    const showEmpty = !isLoading && !error && hasSearched && results.length === 0;

    return (
        <div className="flex flex-col gap-12 size-full justify-between" data-testid="search-layout">
            <CurrentGenerationCta />
            {showError ? (
                <ErrorState />
            ) : showEmpty ? (
                <NoResultsState />
            ) : (
                <JustifiedGallery rowGapPx={8} columnGapPx={8} items={results} isLoading={isLoading}>
                    {({ item, aspectRatio }) => (
                        <ImageResultsCard
                            imageUrl={item.thumbnailUrl}
                            imageAlt={item.title}
                            selected={item.contentId === selectedLibraryImage?.contentId}
                            disabled={isFormDisabled}
                            onSelect={() => handleSelect(item)}
                            style={{ aspectRatio }}
                        />
                    )}
                </JustifiedGallery>
            )}
            {results.length > 0 && !showError && (
                <Pagination
                    resultsPerPage={48}
                    maxPage={totalPages}
                    currentPage={currentPage}
                    goTo={(page) => void goToPage(page)}
                />
            )}
        </div>
    );
};
